import React, {memo} from 'react'
import {useFormContext} from 'react-hook-form'

import {SlotSkeleton} from '../../../../components/mobile'
import {useConfigContext} from '../../../../contexts'
import {FieldName} from '../../../../lib/fieldNames'
import {PickupRoute} from '../../../PickupRoute/PickupRoute'
import {useAddressWidget} from '../../hooks/useAddressWidget'
import {useSelectionDependentComponents} from '../../hooks/useSelectionDependentComponents'
import {getPointLocation} from '../../lib'
import {AddressDrawer} from '../AddressDrawer'
import {AddressPointSlot} from '../AddressPointSlot/AddressPointSlot'
import {AddressDetails} from '../Details/Address'
import {ContactDetails} from '../Details/Contact'
import {usePrefetchAddress} from './hooks/use-prefetch-address'
import {useAddressWidgetProps} from './hooks/useAddressWidgetProps'
import type {Props} from './types'

/**
 * Компонент ввода адреса с дровером, в котором форма с адресом и данными контактного лица.
 */
export const AddressWidget = (props: Props) => {
  const {watch} = useFormContext()
  const mode = watch('selected_submode')
  const isNdd = mode === 'ndd_self_pickup'

  return isNdd ? (
    <PickupRoute config={props.config} name={`${FieldName.Ndd}.route`} />
  ) : (
    <AddressWidgetCommon {...props} />
  )
}

const AddressWidgetCommon = memo((props: Props): React.ReactElement => {
  const {isSource, pointId, addressName, contactsName} = useAddressWidgetProps(props)
  const {initialProps, location} = useConfigContext()
  const pointLocation = getPointLocation({initialProps, location, isSource})

  const {isLoading} = usePrefetchAddress(addressName, isSource, pointId, pointLocation)

  const address = useFormContext().watch(addressName)

  const {handleClose, handleOpen, handleSubmit, isFinalRoutePoint, opened} = useAddressWidget(
    addressName,
    contactsName,
    pointId,
    props.config,
  )

  const {headerTitle} = useSelectionDependentComponents(Boolean(address), props.config)

  if (isLoading) {
    return <SlotSkeleton />
  }

  return (
    <>
      <AddressPointSlot addressWidgetConfig={props.config} name={addressName} onClick={handleOpen} />
      <AddressDrawer
        isFinalRoutePoint={isFinalRoutePoint}
        onClose={handleClose}
        onSubmit={handleSubmit}
        opened={opened}
        title={headerTitle}
      >
        <AddressDetails addressWidgetConfig={props.config} isSource={isSource} name={addressName} pointId={pointId} />
        <ContactDetails isSource={isSource} name={contactsName} pointId={pointId} />
      </AddressDrawer>
    </>
  )
})
